import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Card,
  Grid,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  Dialog,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import Breadcrumbs from "examples/Breadcrumbs";
import WhatsAppFloat from "components/WhatsAppFloat";

import bgBanner from "assets/images/bg-presentation.jpg";

/* ==== Imagens (ajuste os nomes se necessário) ==== */
import ImgCentroHaas from "assets/images/equipamentos/Centro de Usinagem Haas Mini Mill (406 X 305 X 254).png";
import ImgTridimensional from "assets/images/equipamentos/Tridimensional mitutoyo BR-M443.png";
import ImgAgie from "assets/images/equipamentos/Agie Charmilles ACTSPARK SP1.png";
import ImgTornoRomi from "assets/images/equipamentos/Torno Romi Centur 30D (Diâmetro 380 x 1000).png";
import ImgFresaVeker from "assets/images/equipamentos/Fresa Veker 406I (1.150 X 315 X 406).png";
import ImgTornoMagnum from "assets/images/equipamentos/Torno Magnum Cut (Diâmetro 178 x 1000).png";
import ImgRetificaMagnum from "assets/images/equipamentos/Retífica Magnum Cut (470 x 200 x 305).png";
import ImgSerraAcra from "assets/images/equipamentos/Serra de Fita Acra.png";
import ImgDurometro from "assets/images/equipamentos/Durometro rockwell hardness tester.png";
import ImgFornoEDG from "assets/images/equipamentos/Forno EDG 1100 Graus.png";

/* ----------------- Paleta ----------------- */
const BRAND = {
  main: "#036D3D",
  dark: "#004C2A",
  glass: "rgba(1,43,24,.58)",
  line: "rgba(3,109,61,.30)",
};

/* ----------------- Helpers ----------------- */
function SectionContainer({ children, sx }) {
  return (
    <Container sx={{ maxWidth: 1100, width: "100%", mx: "auto", ...sx }}>
      {children}
    </Container>
  );
}
SectionContainer.propTypes = { children: PropTypes.node, sx: PropTypes.object };

const FadeIn = ({ children, show, timeout = 400, sx }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (show) {
      const t = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [show]);
  return (
    <MKBox
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(8px)",
        transition: `opacity ${timeout}ms ease, transform ${timeout}ms ease`,
        ...sx,
      }}
    >
      {children}
    </MKBox>
  );
};
FadeIn.propTypes = { children: PropTypes.node, show: PropTypes.bool, timeout: PropTypes.number, sx: PropTypes.object };

/* ----------------- Dados ----------------- */
const MACHINES = [
  { title: "Centro de Usinagem Haas Mini Mill", spec: "406 × 305 × 254", tag: "Centro de usinagem", image: ImgCentroHaas },
  { title: "Tridimensional Mitutoyo BR-M443", spec: "", tag: "Metrologia", image: ImgTridimensional },
  { title: "Agie Charmilles ACTSPARK SP1", spec: "", tag: "Eletroerosão", image: ImgAgie },
  { title: "Torno Romi Centur 30D", spec: "Ø 380 × 1000", tag: "Torno", image: ImgTornoRomi },
  { title: "Fresa Veker 406I", spec: "1.150 × 315 × 406", tag: "Fresadora", image: ImgFresaVeker },
  { title: "Torno Magnum Cut", spec: "Ø 178 × 1000", tag: "Torno", image: ImgTornoMagnum },
  { title: "Retífica Magnum Cut", spec: "470 × 200 × 305", tag: "Retífica", image: ImgRetificaMagnum },
  { title: "Serra de Fita Acra", spec: "Ø 250 mm (redonda) / 127 × 457 (retangular)", tag: "Serra", image: ImgSerraAcra },
  { title: "Durômetro Rockwell (Hardness Tester)", spec: "", tag: "Metrologia", image: ImgDurometro },
  { title: "Forno EDG", spec: "1100 °C", tag: "Tratamento térmico", image: ImgFornoEDG },
];

/* ----------------- Card de Máquina ----------------- */
function MachineCard({ title, spec, tag, image, onOpenImage }) {
  return (
    <Card
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 3,
        border: `1px solid ${BRAND.line}`,
        background: "linear-gradient(180deg, rgba(3,109,61,.04) 0%, rgba(3,109,61,.02) 100%)",
        transition: "transform .2s ease, box-shadow .2s ease",
        "&:hover": { transform: "translateY(-3px)", boxShadow: "0 10px 28px rgba(0,0,0,.08)" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header (título) */}
      <MKBox sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
        <MKTypography variant="button" sx={{ fontWeight: 800, letterSpacing: ".4px" }}>
          {title}
        </MKTypography>
      </MKBox>

      {/* Miniatura principal */}
      <MKBox
        role="button"
        aria-label={`Abrir imagem grande de ${title}`}
        onClick={() => onOpenImage?.()}
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          mb: 1.25,
          cursor: "zoom-in",
          userSelect: "none",
          outline: "none",
          backgroundColor: "#fff",
        }}
      >
        <MKBox
          component="img"
          src={image}
          alt={title}
          loading="lazy"
          sx={{
            width: "100%",
            height: 160,
            display: "block",
            objectFit: "cover",
          }}
        />
        <MKBox
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0,
            transition: "opacity .2s ease",
            background: "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.35) 100%)",
            "&:hover": { opacity: 1 },
          }}
        />
      </MKBox>

      {/* Especificação */}
      {spec && (
        <MKTypography variant="caption" color="text" sx={{ display: "block", mb: 1 }}>
          {spec}
        </MKTypography>
      )}

      {/* Tag */}
      <Chip
        label={tag}
        size="small"
        variant="outlined"
        sx={{
          mt: "auto",
          borderColor: BRAND.line,
          color: BRAND.dark,
          alignSelf: "flex-start",
        }}
      />
    </Card>
  );
}
MachineCard.propTypes = {
  title: PropTypes.string.isRequired,
  spec: PropTypes.string,
  tag: PropTypes.string,
  image: PropTypes.string,
  onOpenImage: PropTypes.func,
};

/* ----------------- Página ----------------- */
export default function MaquinasEquipamentos() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const parallax = isMobile ? "scroll" : "fixed";

  const [loaded, setLoaded] = useState(false);

  // Lightbox em modo galeria
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const touchStartX = useRef(null);
  const NAVBAR_PT = { xs: 18, md: 25 };

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 120);
    return () => clearTimeout(t);
  }, []);

  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox((l) => ({ ...l, open: false }));

  const next = () =>
    setLightbox((l) => ({ ...l, index: (l.index + 1) % MACHINES.length }));
  const prev = () =>
    setLightbox((l) => ({ ...l, index: (l.index - 1 + MACHINES.length) % MACHINES.length }));

  // teclado: ← → Esc
  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.open]);

  // swipe (touch)
  const onTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const THRESHOLD = 40;
    if (dx > THRESHOLD) prev();
    else if (dx < -THRESHOLD) next();
    touchStartX.current = null;
  };

  const current = MACHINES[lightbox.index];

  return (
    <>
      {/* HERO */}
      <MKBox
        role="img"
        aria-label="Ramlis Machine - Máquinas & Equipamentos"
        sx={{
          position: "relative",
          pt: NAVBAR_PT,
          minHeight: { xs: 450, md: 400 },
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          color: "#fff",
          backgroundImage: `url(${bgBanner})`,
          backgroundSize: "cover",
          backgroundPosition: { xs: "center", md: "center 35%" },
          backgroundAttachment: { xs: "scroll", md: parallax },
          "@media (prefers-reduced-motion: reduce)": { backgroundAttachment: "scroll" },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0, 14, 8, 0.75) 0%, rgba(0,76,42,.55) 45%, rgba(0,76,42,.75) 100%)",
          },
        }}
      >
        <FadeIn show={loaded} timeout={600} sx={{ position: "relative", zIndex: 1, width: "100%" }}>
          <SectionContainer>
            <MKBox
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                px: { xs: 1.5, md: 3 },
                py: { xs: 1, md: 1.25 },
                mx: "auto",
                borderRadius: 2,
                backgroundColor: BRAND.glass,
                backdropFilter: "saturate(1.1) blur(3px)",
                boxShadow: "0 10px 28px rgba(0,76,42,.35)",
              }}
            >
              <MKTypography component="h1" variant="h3" fontWeight="bold" color="white" mb={0.5} sx={{ textShadow: "0 2px 6px rgba(0,76,42,.85)" }}>
                Máquinas & Equipamentos
              </MKTypography>
              <MKTypography variant="body1" color="white" sx={{ opacity: 0.95, textShadow: "0 1px 3px rgba(0,76,42,.8)" }}>
                Estrutura completa para usinagem de precisão e metrologia.
              </MKTypography>
            </MKBox>
          </SectionContainer>
        </FadeIn>
      </MKBox>

      {/* Breadcrumbs */}
      <SectionContainer sx={{ mt: -3, mb: 4 }}>
        <Breadcrumbs
          routes={[{ label: "Home", route: "/" }, { label: "Máquinas & Equipamentos" }]}
          sx={{ "& .MuiBreadcrumbs-separator": { color: BRAND.main } }}
        />
      </SectionContainer>

      {/* Conteúdo principal */}
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
          backgroundColor: "#fff",
        }}
      >
        <FadeIn show={loaded} timeout={700}>
          <SectionContainer sx={{ py: { xs: 3, md: 5 } }}>
            {/* Intro */}
            <MKTypography variant="body2" color="text" paragraph sx={{ lineHeight: 1.85, textAlign: "justify" }}>
              Possuímos uma completa estrutura de equipamentos para atender os mais diversos projetos
              solicitados por nossos clientes. Estrutura eficaz para qualquer trabalho de usinagem de precisão,
              utilizando os seguintes equipamentos:
            </MKTypography>

            {/* Linha separadora */}
            <Divider
              sx={{
                my: { xs: 3, md: 4 },
                height: 2,
                border: "none",
                background:
                  "linear-gradient(90deg, rgba(3,109,61,0) 0%, rgba(3,109,61,.35) 25%, rgba(3,109,61,.5) 50%, rgba(3,109,61,.35) 75%, rgba(3,109,61,0) 100%)",
              }}
            />

            {/* Grid de máquinas */}
            <Grid container spacing={{ xs: 2.25, md: 3 }}>
              {MACHINES.map((m, idx) => (
                <Grid key={m.title} item xs={12} sm={6} md={4}>
                  <MachineCard
                    {...m}
                    onOpenImage={() => openLightbox(idx)}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Linha separadora inferior opcional */}
            <Divider
              sx={{
                mt: { xs: 4, md: 6 },
                height: 2,
                border: "none",
                background:
                  "linear-gradient(90deg, rgba(3,109,61,0) 0%, rgba(3,109,61,.35) 25%, rgba(3,109,61,.5) 50%, rgba(3,109,61,.35) 75%, rgba(3,109,61,0) 100%)",
              }}
            />

          </SectionContainer>
        </FadeIn>
      </Card>

      {/* Lightbox / Galeria (fundo branco) */}
      <Dialog
        open={lightbox.open}
        onClose={closeLightbox}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#fff",         
            borderRadius: 2,
          },
        }}
      >
        <MKBox
          sx={{
            position: "relative",
            bgcolor: "#fff",
            p: { xs: 1.5, md: 2 },
          }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Fechar */}
          <IconButton
            aria-label="Fechar"
            onClick={closeLightbox}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Anterior */}
          <IconButton
            aria-label="Anterior"
            onClick={prev}
            sx={{
              position: "absolute",
              top: "50%",
              left: 8,
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: "rgba(0,0,0,0.04)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          {/* Próxima */}
          <IconButton
            aria-label="Próxima"
            onClick={next}
            sx={{
              position: "absolute",
              top: "50%",
              right: 8,
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: "rgba(0,0,0,0.04)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
            }}
          >
            <ChevronRightIcon />
          </IconButton>

          {/* Imagem atual */}
          <MKBox
            component="img"
            src={current?.image}
            alt={current?.title}
            sx={{
              width: "100%",
              height: "auto",
              display: "block",
              maxHeight: { xs: "75vh", md: "85vh" },
              objectFit: "contain",
              backgroundColor: "#fff", // garante fundo branco atrás da imagem
              borderRadius: 1,
            }}
          />

          {/* Título */}
          {current?.title && (
            <MKTypography
              variant="caption"
              sx={{ color: "text.primary", display: "block", px: 0.5, pt: 1, textAlign: "center" }}
            >
              {current.title}
            </MKTypography>
          )}
        </MKBox>
      </Dialog>

      <WhatsAppFloat />
    </>
  );
}
